import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Helper to normalize thread objects from the API.
 */
const normalizeThread = (t) => {
  if (!t) return t;
  const personName =
    t.chatWithName || t.threadTitle || t.name || t.tenantName || t.landlordName || t.hostName || 'Conversation';
  const propertyName = t.chatWithSubtitle || t.propertyName || t.property || '';
  const displayLabel = propertyName ? `${personName} - ${propertyName}` : personName;
  return {
    ...t,
    id: t.id || t._id || t.threadId || null,
    displayName: personName,
    displaySubtitle: propertyName,
    displayLabel,
    displayMsg: t.lastMessagePreview || t.lastMsg || '',
    displayTime: t.lastMessageAt ? new Date(t.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (t.time || '')
  };
};

export const getThreadDisplayLabel = (thread) => {
  if (!thread) return 'Conversation';
  if (thread.displayLabel) return thread.displayLabel;
  const personName = thread.displayName || 'Conversation';
  const propertyName = thread.displaySubtitle || thread.propertyName || thread.property || '';
  return propertyName ? `${personName} - ${propertyName}` : personName;
};

const normalizeThreadContext = (context) => {
  const value = String(context || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (value === 'AIRBNB_HOST' || value === 'HOST') return 'BNB_HOST';
  return value;
};

/**
 * Helper to normalize message objects from the API.
 */
const normalizeMessage = (m) => {
  if (!m) return m;
  return {
    ...m,
    id: m.id || m._id || null,
    text: m.body || m.text || '',
    timestamp: m.createdAt || m.timestamp || new Date().toISOString()
  };
};

/**
 * Fetch all chat threads for the current user.
 */
export const getMyThreads = async (context = null) => {
  try {
    let url = `${API_BASE_URL}/api/chat/threads`;
    const normalizedContext = normalizeThreadContext(context);
    // If context is provided and is not a global admin role, append it to filter
    if (normalizedContext && !['ADMIN', 'SYSTEM_ADMIN'].includes(normalizedContext)) {
      url += `?context=${encodeURIComponent(normalizedContext)}`;
    }
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch threads');
    }

    const threads = Array.isArray(data) ? data : (data?.data || data?.content || []);
    return threads.map(normalizeThread);
  } catch (error) {
    console.error('Get my threads error:', error);
    throw error;
  }
};

const normalizeId = (id) => {
  const value = String(id ?? '').trim();
  return value || null;
};

const uniqueIds = (ids) => ids
  .map(normalizeId)
  .filter(Boolean)
  .filter((value, index, self) => self.indexOf(value) === index);

const firstId = (...ids) => uniqueIds(ids)[0] || null;

const buildThreadQuery = (propertyId, options = {}, targetParam = 'tenantUserId') => {
  const params = new URLSearchParams();
  if (propertyId != null && propertyId !== '') {
    params.set('propertyId', String(propertyId));
  }

  const targetUserId =
    options?.tenantUserId ||
    options?.guestUserId ||
    options?.userId ||
    options?.tenantId ||
    options?.guestId;

  if (targetUserId != null && targetUserId !== '') {
    params.set(targetParam, String(targetUserId));
  }

  return params.toString();
};

const getNestedId = (record, ...path) => {
  let current = record;
  for (const key of path) {
    if (!current || typeof current !== 'object') return null;
    current = current[key];
  }
  return current;
};

const getParticipantIds = (thread) => {
  const participantLists = [
    thread?.participants,
    thread?.users,
    thread?.members,
    thread?.threadParticipants,
  ].filter(Array.isArray);

  return participantLists.flatMap((list) =>
    list.flatMap((participant) => [
      participant?.userId,
      participant?.userID,
      participant?.participantUserId,
      getNestedId(participant, 'user', 'id'),
      getNestedId(participant, 'user', 'userId'),
    ])
  );
};

const hasGroupParticipants = (thread) => {
  const participantLists = [
    thread?.participants,
    thread?.users,
    thread?.members,
    thread?.threadParticipants,
  ].filter(Array.isArray);

  return (
    participantLists.some((list) => list.length > 2) ||
    Boolean(thread?.isGroup || thread?.groupChat || thread?.broadcast) ||
    String(thread?.threadType || thread?.type || '').toUpperCase().includes('GROUP') ||
    String(thread?.chatType || '').toUpperCase().includes('GROUP')
  );
};

const getThreadUserIds = (thread) => {
  if (!thread) return [];
  return uniqueIds([
    thread?.chatWithUserId,
    thread?.tenantUserId,
    thread?.tenantId,
    thread?.guestUserId,
    thread?.guestId,
    thread?.otherUserId,
    thread?.otherParticipantId,
    thread?.participantUserId,
    thread?.userId,
    thread?.landlordUserId,
    thread?.hostUserId,
    getNestedId(thread, 'tenant', 'userId'),
    getNestedId(thread, 'tenant', 'id'),
    getNestedId(thread, 'guest', 'userId'),
    getNestedId(thread, 'guest', 'id'),
    getNestedId(thread, 'chatWith', 'userId'),
    getNestedId(thread, 'chatWith', 'id'),
    ...getParticipantIds(thread),
  ]);
};

const getThreadCounterpartyUserId = (thread, currentUserId = null) => {
  const ids = getThreadUserIds(thread);
  if (!ids.length) return null;
  if (currentUserId != null) {
    const current = String(currentUserId).trim();
    const others = ids.filter((id) => id !== current);
    if (others.length > 0) return others[0];
    return null;
  }
  return ids[0];
};

const threadHasUser = (thread, userId) => {
  const target = normalizeId(userId);
  if (!target) return false;
  return getThreadUserIds(thread).includes(target);
};

const isDirectThreadForTarget = (thread, targetUserId, currentUserId) => {
  if (!threadHasUser(thread, targetUserId)) return false;
  if (hasGroupParticipants(thread)) return false;
  const target = normalizeId(targetUserId);
  const current = normalizeId(currentUserId);
  if (target && current && target === current) return false;
  const ids = getThreadUserIds(thread);
  const unexpectedUsers = ids.filter((id) => id !== target && id !== current);
  if (unexpectedUsers.length > 0) return false;
  return true;
};

const assertDirectThreadForTarget = (thread, targetUserId, currentUserId, errorMessage) => {
  if (!thread || !targetUserId) return;
  if (hasGroupParticipants(thread)) {
    throw new Error(errorMessage);
  }

  const target = normalizeId(targetUserId);
  const current = normalizeId(currentUserId);
  if (target && current && target === current) {
    throw new Error(errorMessage);
  }

  const ids = getThreadUserIds(thread);
  const unexpectedUsers = ids.filter((id) => id !== target && id !== current);
  if (unexpectedUsers.length > 0) {
    throw new Error(errorMessage);
  }
};

const enrichThreadForGuest = (thread, guestName, guestUserId, options = {}) => {
  if (!thread) return thread;
  const normalized = normalizeThread(thread);
  const targetUserId = guestUserId || getThreadCounterpartyUserId(thread) || normalized.chatWithUserId;
  const base = {
    ...normalized,
    propertyId: options?.propertyId || normalized.propertyId,
    chatMode: options?.chatMode || normalized.chatMode,
    chatWithUserId: targetUserId,
    intendedRecipientUserId: targetUserId,
    recipientUserId: targetUserId,
    ...(options?.chatMode === 'landlord' ? { tenantUserId: targetUserId } : {}),
    ...(options?.chatMode === 'bnb' ? { guestUserId: targetUserId } : {}),
  };

  if (guestName) {
    const propertyName = base.displaySubtitle || '';
    return {
      ...base,
      chatWithName: guestName,
      displayName: guestName,
      displayLabel: propertyName ? `${guestName} - ${propertyName}` : guestName,
    };
  }

  return base;
};

/**
 * Find an existing tenant ↔ landlord/agent thread for a property + tenant.
 */
export const findTenantLandlordThread = async (propertyId, tenantUserId, options = {}) => {
  if (!propertyId || !tenantUserId) return null;
  const currentUserId = options?.currentUserId || null;
  try {
    const threads = await getMyThreads();
    return (
      threads.find((thread) => {
        if (String(thread?.propertyId || '') !== String(propertyId)) return false;
        return isDirectThreadForTarget(thread, tenantUserId, currentUserId);
      }) || null
    );
  } catch (error) {
    console.error('Find tenant-landlord thread error:', error);
    return null;
  }
};

/**
 * Find an existing tenant/guest ↔ BNB host thread for a property + guest.
 */
export const findBnbHostThread = async (propertyId, tenantUserId, options = {}) => {
  if (!propertyId || !tenantUserId) return null;
  const currentUserId = options?.currentUserId || null;
  try {
    const threads = await getMyThreads();
    return (
      threads.find((thread) => {
        if (String(thread?.propertyId || '') !== String(propertyId)) return false;
        return isDirectThreadForTarget(thread, tenantUserId, currentUserId);
      }) || null
    );
  } catch (error) {
    console.error('Find BNB host thread error:', error);
    return null;
  }
};

/**
 * Open or get an existing tenant ↔ landlord/agent thread for a property.
 * When called by a landlord/agent, pass tenantUserId so the correct tenant is targeted.
 */
export const getOrCreateTenantLandlordThread = async (propertyId, options = {}) => {
  try {
    const tenantUserId =
      firstId(options?.tenantUserId, options?.guestUserId, options?.userId, options?.tenantId, options?.guestId);
    const guestName = options?.guestName || options?.tenantName || null;
    const findOptions = { currentUserId: options?.currentUserId || null };

    if (tenantUserId) {
      const existing = await findTenantLandlordThread(propertyId, tenantUserId, findOptions);
      if (existing) return enrichThreadForGuest(existing, guestName, tenantUserId, { chatMode: 'landlord', propertyId });
    }

    const query = buildThreadQuery(propertyId, { ...options, tenantUserId }, 'tenantUserId');
    const url = `${API_BASE_URL}/api/chat/threads/tenant-landlord?${query}`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to open conversation');
    }

    const thread = data?.data || data;
    assertDirectThreadForTarget(
      thread,
      tenantUserId,
      options?.currentUserId,
      'Could not open a direct tenant conversation for this request.'
    );

    return enrichThreadForGuest(thread, guestName, tenantUserId, { chatMode: 'landlord', propertyId });
  } catch (error) {
    console.error('Get/Create Tenant-Landlord thread error:', error);
    throw error;
  }
};

/**
 * Open or get an existing tenant/guest ↔ BNB host thread for a property.
 * When called by a BNB host, pass tenantUserId/guestUserId so the correct guest is targeted.
 */
export const getOrCreateBnbHostThread = async (propertyId, options = {}) => {
  try {
    const tenantUserId =
      firstId(options?.guestUserId, options?.tenantUserId, options?.userId, options?.guestId, options?.tenantId);
    const guestName = options?.guestName || options?.tenantName || null;
    const findOptions = { currentUserId: options?.currentUserId || null };

    if (tenantUserId) {
      const existing = await findBnbHostThread(propertyId, tenantUserId, findOptions);
      if (existing) return enrichThreadForGuest(existing, guestName, tenantUserId, { chatMode: 'bnb', propertyId });
    }

    const params = new URLSearchParams(buildThreadQuery(propertyId, { ...options, guestUserId: tenantUserId }, 'guestUserId'));
    params.set('tenantUserId', String(tenantUserId));
    const query = params.toString();
    const url = `${API_BASE_URL}/api/chat/threads/tenant-bnb-host?${query}`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to open BNB host conversation');
    }

    const thread = data?.data || data;
    assertDirectThreadForTarget(
      thread,
      tenantUserId,
      options?.currentUserId,
      'Could not open a direct guest conversation for this booking.'
    );

    return enrichThreadForGuest(thread, guestName, tenantUserId, { chatMode: 'bnb', propertyId });
  } catch (error) {
    console.error('Get/Create BNB host thread error:', error);
    throw error;
  }
};

/**
 * Access system support (Tenant ↔ Admin) thread.
 */
export const getOrCreateTenantAdminThread = async () => {
  try {
    const url = `${API_BASE_URL}/api/chat/threads/tenant-admin`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to open support thread');
    }

    return normalizeThread(data?.data || data);
  } catch (error) {
    console.error('Get/Create Tenant-Admin thread error:', error);
    throw error;
  }
};

/**
 * Access system support (Landlord ↔ Admin) thread.
 */
export const getOrCreateLandlordAdminThread = async () => {
  try {
    const url = `${API_BASE_URL}/api/chat/threads/landlord-admin`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to open landlord support thread');
    }

    return normalizeThread(data?.data || data);
  } catch (error) {
    console.error('Get/Create Landlord-Admin thread error:', error);
    throw error;
  }
};

/**
 * Access system support (BNB Host ↔ Admin) thread.
 */
export const getOrCreateBnbHostAdminThread = async () => {
  try {
    const url = `${API_BASE_URL}/api/chat/threads/bnb-host-admin`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to open BNB host support thread');
    }

    return normalizeThread(data?.data || data);
  } catch (error) {
    console.error('Get/Create BNB-Host-Admin thread error:', error);
    throw error;
  }
};

/**
 * Mark a specific thread as read for the current user.
 */
export const markThreadAsRead = async (threadId) => {
  try {
    const url = `${API_BASE_URL}/api/chat/threads/${threadId}/read`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to mark thread as read');
    }

    return data;
  } catch (error) {
    console.error(`Mark thread ${threadId} as read error:`, error);
    throw error;
  }
};

/**
 * Fetch messages for a specific chat thread with pagination.
 */
export const getThreadMessages = async (threadId, page = 0, size = 30) => {
  try {
    const url = `${API_BASE_URL}/api/chat/threads/${threadId}/messages?page=${page}&size=${size}`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch messages');
    }

    const payload = data?.data || data;
    const messages = Array.isArray(payload) ? payload : (payload?.content || payload?.data || []);
    return messages.map(normalizeMessage);
  } catch (error) {
    console.error(`Get thread messages error for ${threadId}:`, error);
    throw error;
  }
};

/**
 * Send a new message to a specific thread.
 */
export const sendMessage = async (threadId, messageData) => {
  try {
    const url = `${API_BASE_URL}/api/chat/threads/${threadId}/messages`;
    const recipientUserId = normalizeId(
      messageData.recipientUserId ||
      messageData.receiverUserId ||
      messageData.targetUserId ||
      messageData.chatWithUserId ||
      messageData.tenantUserId ||
      messageData.guestUserId
    );
    const payload = {
      body: messageData.text || messageData.body,
      ...messageData
    };
    delete payload.text;

    if (recipientUserId) {
      payload.recipientUserId = recipientUserId;
      payload.receiverUserId = recipientUserId;
      payload.targetUserId = recipientUserId;
      payload.chatWithUserId = recipientUserId;
    }

    const response = await apiClient(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }, true);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to send message');
    }

    return normalizeMessage(data?.data || data);
  } catch (error) {
    console.error('Send message error:', error);
    throw error;
  }
};
